import type { LocalTime } from '@app/api/promotion-service/model/localTime';

/** API date field (YYYY-MM-DD). */
export function formatApiDate(value: unknown): string | undefined {
  if (!value) {
    return undefined;
  }
  const date = value instanceof Date ? value : new Date(value as string);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Backend expects java.time.LocalTime as JSON string "HH:mm:ss",
 * not OpenAPI object { hour, minute, second, nano }.
 */
export function formatApiLocalTime(value: unknown): string | undefined {
  const parsed = parseTimeParts(value);
  if (!parsed) {
    return undefined;
  }
  const h = String(parsed.hour).padStart(2, '0');
  const m = String(parsed.minute).padStart(2, '0');
  const s = String(parsed.second).padStart(2, '0');
  return `${h}:${m}:${s}`;
}

/** @deprecated Use formatApiLocalTime for API payloads. */
export function localTimeFromPicker(value: unknown): LocalTime | undefined {
  const formatted = formatApiLocalTime(value);
  if (!formatted) {
    return undefined;
  }
  const parsed = parseTimeParts(formatted);
  if (!parsed) {
    return undefined;
  }
  return {
    hour: parsed.hour,
    minute: parsed.minute,
    second: parsed.second,
    nano: 0,
  };
}

export function dateFromApiDate(dateValue?: string): Date | null {
  if (!dateValue) {
    return null;
  }
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) {
    return null;
  }
  d.setHours(0, 0, 0, 0);
  return d;
}

export function dateFromLocalTime(timeValue?: LocalTime | string): Date | null {
  const parsed = parseTimeParts(timeValue);
  if (!parsed) {
    return null;
  }
  const d = new Date(2000, 0, 1);
  d.setHours(parsed.hour, parsed.minute, parsed.second, 0);
  return d;
}

function parseTimeParts(
  timeValue?: LocalTime | string | unknown,
): { hour: number; minute: number; second: number } | null {
  if (!timeValue) {
    return null;
  }
  if (timeValue instanceof Date) {
    if (Number.isNaN(timeValue.getTime())) {
      return null;
    }
    return {
      hour: timeValue.getHours(),
      minute: timeValue.getMinutes(),
      second: timeValue.getSeconds(),
    };
  }
  if (typeof timeValue === 'string') {
    const m = /^(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/.exec(timeValue.trim());
    if (!m) {
      return null;
    }
    return {
      hour: Number(m[1]),
      minute: Number(m[2]),
      second: Number(m[3] ?? '0'),
    };
  }
  if (typeof timeValue === 'object') {
    const t = timeValue as LocalTime;
    return {
      hour: t.hour ?? 0,
      minute: t.minute ?? 0,
      second: t.second ?? 0,
    };
  }
  return null;
}
